import { z } from "zod";
const revision = z
  .string()
  .min(1)
  .max(128)
  .refine((value) => Number.isFinite(Date.parse(value)));
export const ApprovalPolicyRuleSchema = z.object({
  id: z.string().min(1).max(128),
  name: z.string().min(1).max(500),
  direction: z.enum(["INBOUND", "OUTBOUND", "ALL"]),
  condition: z.object({
    type: z.enum(["AMOUNT", "PARTNER", "CATEGORY"]),
    operator: z.enum(["GT", "LT", "EQ", "CONTAINS"]),
    value: z.union([z.string().max(4096), z.number().finite()]),
  }),
  action: z.object({
    type: z.enum(["REQUIRE_APPROVAL", "AUTO_APPROVE", "CHAIN_APPROVAL"]),
    approvers: z.array(z.string().max(128)).max(100).optional(),
    levels: z.number().int().positive().optional(),
  }),
  enabled: z.boolean(),
  priority: z.number().int(),
});
export const ApprovalPolicyEditableRuleSchema =
  ApprovalPolicyRuleSchema.superRefine((rule, ctx) => {
    const issue = (path: (string | number)[]) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Review this field",
        path,
      });
    if (!rule.name.trim()) issue(["name"]);
    if (rule.priority < 1) issue(["priority"]);
    if (rule.condition.type === "AMOUNT") {
      if (typeof rule.condition.value !== "number")
        issue(["condition", "value"]);
      if (rule.condition.operator === "CONTAINS")
        issue(["condition", "operator"]);
    } else {
      if (
        typeof rule.condition.value !== "string" ||
        !rule.condition.value.trim()
      )
        issue(["condition", "value"]);
      if (!["EQ", "CONTAINS"].includes(rule.condition.operator))
        issue(["condition", "operator"]);
    }
    if (rule.action.type === "CHAIN_APPROVAL" && !rule.action.levels)
      issue(["action", "levels"]);
    if (rule.action.approvers?.some((value) => !value.trim()))
      issue(["action", "approvers"]);
  });
const uniqueRules = <T extends z.ZodTypeAny>(rule: T) =>
  z
    .array(rule)
    .max(1000)
    .superRefine((rules, ctx) => {
      const ids = new Set<string>();
      rules.forEach((item, index) => {
        if (ids.has(item.id))
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Duplicate rule",
            path: [index, "id"],
          });
        ids.add(item.id);
      });
    });
export const ApprovalPolicyDraftSchema = z.object({
  tenantId: z.string().min(1),
  data: uniqueRules(ApprovalPolicyRuleSchema),
  updatedAt: revision.nullable(),
  executionSource: z.literal("WORKFLOW_DEFINITION"),
});
export const ApprovalPolicySaveSchema = z.object({
  rules: uniqueRules(ApprovalPolicyEditableRuleSchema),
  expectedUpdatedAt: revision.nullable(),
});
export type ApprovalPolicyRule = z.infer<typeof ApprovalPolicyRuleSchema>;
export type ApprovalPolicyDraft = z.infer<typeof ApprovalPolicyDraftSchema>;
export type ApprovalPolicySave = z.infer<typeof ApprovalPolicySaveSchema>;
